import React from "react";
import _ from "lodash";
import moment from "moment";
import getDayRangeString from "../../../utils/day-range-string";
import Chart from "../../../parts/chart";
import DateInput from "../../../parts/inputs/date-input";
import ToggleLinksInput from "../../../parts/inputs/toggle-links-input";
import ShareModal from "../../../parts/modals/share";
import MetricsBackButton from "./metrics-back-button";
import set from "@rill/set";
import { getChartLabels } from "../../../utils/chart-labels";

import { ZONES, PERIOD_FORMATS, DATE_FORMATS } from "../../../constants";
import NavigationComponent from "../calendar/components/NavigationComponent";
import SvgIcon from "../../../parts/SvgIcon";
import { ReportsBackHandler } from "../calendar/components/ReportsBackHandler";
import { AthleteLinks } from "../../../parts/athlete-links";
import getAvatarUrl from "../../../utils/get-avatar-url";
import { AthleteSelect } from "../../../parts/inputs/athlete-select-new";
import ActivityDrawer from "../../../parts/activity-drawer/ActivityDrawer";
import { useRillContext } from "../../../rill/RillReact/share/base";
import { useTranslation } from "react-i18next";
import { isAdminOrOwner } from "../../../utils/organization-permissions-helpers";

const { DATE_PICKER_FORMAT } = DATE_FORMATS;

function View(props) {
  const { locals } = useRillContext();
  const { t } = useTranslation("athlete-portal");

  const {
    exercisesVolumeGroupZones,
    exercisesVolumeMean,
    query,
    search,
    athlete,
    pathname,
  } = locals;
  const { period, units, stack, type } = query;
  const organizationCoach = query.organizationCoach || undefined;

  const period_short = PERIOD_FORMATS[period];
  const selectedMetrics =
    query.type === "total"
      ? exercisesVolumeGroupZones
      : _.pick(exercisesVolumeGroupZones, [query.type]);
  const keys = _(selectedMetrics)
    .flatMap(_.keys)
    .uniq()
    .map(Number)
    .sortBy()
    .value();

  const labels = getChartLabels(keys, query, period);

  const selectedMetricsGroups = _.values(selectedMetrics);
  const volumeSeries = _.map(ZONES, (zone) => {
    return {
      name: zone,
      className: stack ? zone : type,
      data: _.map(
        keys,
        (key) =>
          locals.fromMetric(
            _.sumBy(selectedMetricsGroups, `${key}.${zone}`),
            units
          ) || 0
      ),
    };
  });
  const totalVolumes = _.map(keys, (k, i) =>
    _.sum(_.map(volumeSeries, `data.${i}`))
  );

  const baselineVolume = locals.fromMetric(
    _.get(exercisesVolumeMean, query.type),
    units
  );
  const baselineSeries = _.map(keys, () => baselineVolume);
  const maxValue = _.max(totalVolumes.concat(baselineVolume));

  return (
    <main role="reports-wrapper" className="Main Metrics">
      <NavigationComponent
        query={query}
        pathName={"/athlete-portal/metrics"}
        textKey="nav.calendarDayView"
        classNames="mobile-hidden"
      />
      <div className="athlete-section-header only-show-mobile">
        <AthleteLinks
          athlete={athlete}
          pathName={pathname}
          classNames={"only-show-mobile"}
          query={query}
        />
        <div className="athlete-select dropdown-athlete">
          <div
            className="avatar"
            style={{
              backgroundImage: `url(${getAvatarUrl(athlete?._id)})`,
            }}
          />
          <AthleteSelect locals={locals} />
        </div>{" "}
      </div>
      <article role="report-content" id="strength-metrics">
        <ReportsBackHandler
          title={t("metrics.strengthMetrics")}
          locals={locals}
          customBackUrl={`/athlete-portal/metrics/${search}`}
        />
        <header>
          <hgroup className="metrics-title">
            <MetricsBackButton
              url={`/athlete-portal/metrics/${search}`}
              classNames="mobile-hidden"
            />
            <SvgIcon
              src="/img/new-icons/baseline-vol-rel-intensity-ico.svg"
              className="metrics-icon mobile-hidden"
            />
            <div>
              <SvgIcon
                src="/img/new-icons/baseline-vol-rel-intensity-ico.svg"
                className="metrics-icon only-show-mobile"
              />
              <h3>{t("metrics.baselineVolumeRelativeIntensity")}</h3>
              <ToggleLinksInput
                name="units"
                options={[
                  { text: t("metricsDrawer.kg"), value: "metric" },
                  { text: t("metricsDrawer.lbs"), value: "imperial" },
                ]}
                className={"only-show-mobile"}
              />
              <p className="p4 mobile-hidden">
                {t("baselineVolumeRelativeIntensity.description")}
              </p>
            </div>
            <p className="p4 only-show-mobile">
              {t("baselineVolumeRelativeIntensity.description")}
            </p>
          </hgroup>
          <hgroup className="metrics-dates">
            <div role="calendar-date-selectors">
              <DateInput
                name="start"
                placeholder={t("reports.startDate")}
                value={query.start}
                max={query.end}
                onChange={(date) => handleDayChange(date, "start")}
              />
              <span>{t("reports.to")}</span>
              <DateInput
                name="end"
                placeholder={t("reports.endDate")}
                value={query.end}
                min={query.start}
                onChange={(date) => handleDayChange(date, "end")}
                alignRight={true}
              />
            </div>
            <div>
              <ToggleLinksInput
                name="period"
                options={[
                  { text: t("calendar.month"), value: "month" },
                  { text: t("calendar.week"), value: "week" },
                  { text: t("calendar.day"), value: "day" },
                ]}
                activePrimary={true}
              />
            </div>
          </hgroup>
          <hgroup className="metrics-data-options">
            <ToggleLinksInput
              name="units"
              options={[
                { text: t("metricsDrawer.kg"), value: "metric" },
                { text: t("metricsDrawer.lbs"), value: "imperial" },
              ]}
              className={"mobile-hidden"}
            />
            <ToggleLinksInput
              name="baselineTimeSpan"
              options={[
                { text: t("strengthMetricsShared.sixMonths"), value: "6-months" },
                { text: t("strengthMetricsShared.threeMonths"), value: "3-months" },
                { text: t("strengthMetricsShared.oneMonth"), value: "1-month" },
              ]}
              className={"mobile-hidden"}
            />
            <ToggleLinksInput
              className="tabs"
              name="type"
              options={[
                { text: t("strengthMetricsShared.total"), value: "total" },
                { text: t("strengthMetricsShared.squat"), value: "squat" },
                { text: t("strengthMetricsShared.bench"), value: "bench" },
                { text: t("strengthMetricsShared.deadlift"), value: "deadlift" },
              ]}
            />
          </hgroup>
        </header>
        <div className="Chart-Frame bar-chart">
          <div className="body">
            {!_.isEmpty(selectedMetrics) && (
              <div>
                <div className="legend alt">
                  <div className="border">
                    <a
                      className="mobile-hidden"
                      href={`/actions/refresh?stack=${!stack}`}
                    >
                      <input type="checkbox" checked={stack} readOnly={true} />
                    </a>

                    {_.map(ZONES, (zone) => {
                      return (
                        <div key={zone} className={`bar series ${zone}`}>
                          {_.capitalize(zone)}
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div className="axis-labels">
                  <div className="left">{t("strengthMetricsShared.volumeAxisLabel")}</div>
                </div>
                <div className="chart-fullsize">
                  <div className="labels">
                    <Chart
                      type="Bar"
                      only="label"
                      data={{ series: volumeSeries }}
                      options={{
                        width: "60px",
                        height: "380px",
                        stackBars: true,
                        axisX: { showGrid: false, showLabel: false },
                        axisY: {
                          showGrid: false,
                          showLabel: true,
                          onlyInteger: true,
                          scaleMinSpace: 30,
                          high: maxValue,
                          low: 0,
                        },
                      }}
                    />
                  </div>

                  <div className="data">
                    <Chart
                      type="Bar"
                      data={{
                        labels: labels,
                        series: volumeSeries,
                      }}
                      options={{
                        width: `${_.size(keys) * 40}px`,
                        height: "380px",
                        stackBars: true,
                        chartPadding: { left: 35, right: 75 },
                        axisX: { showGrid: false, showLabel: true },
                        axisY: {
                          low: 0,
                          high: maxValue,
                          showGrid: true,
                          showLabel: false,
                          scaleMinSpace: 30,
                        },
                      }}
                      tooltip={(item, i) => {
                        const key = keys[i];
                        const date = moment
                          .utc(query.start, DATE_PICKER_FORMAT)
                          .add(key, period);
                        const dayRange = getDayRangeString(
                          [
                            date,
                            date.clone().add(1, period).subtract(1, "day"),
                          ],
                          query
                        );
                        const totalVolume = totalVolumes[i];
                        return (
                          <div className="tooltip" style={{ left: item.x }}>
                            <div className="details">
                              <div>
                                <b>{dayRange}</b>
                              </div>

                              <div>
                                {t("baselineVolumeRelativeIntensity.tooltipVolume")}{" "}
                                <b>{locals.withWeight(totalVolume, units)}</b>
                              </div>
                              <div>
                                {t("baselineVolumeRelativeIntensity.tooltipBaseline")}{" "}
                                <b>
                                  {locals.withWeight(baselineVolume, units)}
                                </b>
                              </div>
                              {_.map(volumeSeries, (series) => {
                                return series.data[i] ? (
                                  <div>
                                    <b>{_.capitalize(series.name)}</b>{" "}
                                    {_.round(
                                      (series.data[i] / totalVolume) * 100,
                                      2
                                    )}
                                    %
                                  </div>
                                ) : (
                                  false
                                );
                              })}
                            </div>
                          </div>
                        );
                      }}
                    />
                    {_.size(keys) >= 2 && (
                      <Chart
                        type="Line"
                        data={{ series: [baselineSeries] }}
                        options={{
                          width: `${_.size(keys) * 40}px`,
                          height: "380px",
                          showPoint: false,
                          lineSmooth:
                            process.browser &&
                            require("chartist").Interpolation.simple(),
                          chartPadding: { left: 35, right: 75 },
                          axisX: { showGrid: false, showLabel: false },
                          axisY: {
                            low: 0,
                            high: maxValue,
                            showGrid: false,
                            showLabel: false,
                          },
                        }}
                      />
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </article>
    </main>
  );
}

const handleDayChange = (date, type) => {
  // Creating the new query params (type=date) e.g.: start=June 21, 2024
  const searchString = `${encodeURIComponent(type)}=${encodeURIComponent(
    date
  )}`;

  // Creating a new <a> element to navigate to the new URL throught it
  // (tried with submit-form and didn't work)
  var linkElement = document.createElement("a");

  // Setting its href attribute to the new URL
  linkElement.href = `/actions/refresh?${searchString}`;

  // Appending it to the body and simulating the click
  document.body.appendChild(linkElement);
  linkElement.click();
  document.body.removeChild(linkElement);
};

const BaselineVolumeRelativeIntensity = (app) => {
  app.get(
    "/baseline-volume-relative-intensity",
    set("heading", "Baseline Volume & Relative Intensity"),
    async ({ req, res, load, locals, api, fail }) => {
      const { query } = req;

      const isAllowToViewAllOrganizationsAthletes = isAdminOrOwner(
        locals.organizationRoleName
      );

      if (
        locals.activeUser.organizationId &&
        isAllowToViewAllOrganizationsAthletes &&
        !locals.allAthletesFromOrganization
      ) {
        await load("allAthletesFromOrganization");
      }

      _.defaults(req.query, {
        type: "total",
        stack: true,
        period: "day",
        units: locals.units,
        baselineTimeSpan: "6-months",
        start: moment.utc().startOf("month").format(DATE_PICKER_FORMAT),
        end: moment.utc().endOf("month").format(DATE_PICKER_FORMAT),
      });

      await Promise.all([
        load("exercisesVolumeGroupZones", {
          start: moment
            .utc(query.start, DATE_PICKER_FORMAT)
            .startOf("day")
            .toDate(),
          end: moment.utc(query.end, DATE_PICKER_FORMAT).endOf("day").toDate(),
          period: query.period,
          proxyAthlete: query.athlete,
          reqSource: "metrics",
        }),
        load("exercisesVolumeMean", {
          period: query.period,
          timeSpan: query.baselineTimeSpan,
          day: moment.utc(query.end, DATE_PICKER_FORMAT).endOf("day").toDate(),
          proxyAthlete: query.athlete,
          reqSource: "metrics",
        }),
      ]);

      const modal = _.get(query, "modal", {});

      if (modal.type === "share" && !modal.snapshot) {
        // create share link
        try {
          modal.snapshot = (
            await (
              await api("snapshot", {
                method: "POST",
                body: {
                  user: {
                    name: locals.athlete.name,
                    registered: locals.athlete.registered,
                    avatar: locals.athlete.avatar,
                    units: locals.athlete.units,
                  },
                  type: "baseline-volume-relative-intensity",
                  data: JSON.stringify(
                    _.chain(locals)
                      .pick([
                        "exercisesVolumeGroupZones",
                        "exercisesVolumeMean",
                      ])
                      .assign({
                        period: query.period,
                        start: query.start,
                        end: query.end,
                      })
                      .value()
                  ),
                },
              })
            ).json()
          )._id;

          modal.date = getDayRangeString([
            moment.utc(query.start, DATE_PICKER_FORMAT),
            moment.utc(query.end, DATE_PICKER_FORMAT),
          ]);
          return res.refreshWith({ modal });
        } catch (e) {
          console.error(e.stack || e);
          fail(400, "Error creating share link.");
        }
      }

      locals.modal = {
        share: <ShareModal />,
        "activity-feed": <ActivityDrawer />,
      }[modal.type];

      res.body = <View />;
    }
  );
};

export default BaselineVolumeRelativeIntensity;
